import React, { useState, useEffect } from 'react';
import axios from 'axios';

function AddModal() {

  // ── Form state ─────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    productId: '',
    productName: '',
    modalName: '',
    description: '',
    section: '',
    documents: {
      documentType: 'link',      document: null,      documentUrl: '',
      bookDocumentType: 'link',  bookDocument: null,  bookDocumentUrl: '',
      referenceBookType: 'link', referenceBook: null, referenceBookUrl: '',
      socialMediaType: 'link',   socialMedia: null,   socialMediaUrl: '',
      websiteType: 'link',       website: null,       websiteUrl: '',
      pptType: 'link',           ppt: null,           pptUrl: ''
    }
  });

  // ── Edit / Delete state ─────────────────────────────────────────────────────
  // editModalId  → the ID of the modal currently being edited (null = Add mode)
  // isEditMode   → true when form is loaded with existing modal data
  const [editModalId,       setEditModalId]       = useState(null);
  const [isEditMode,        setIsEditMode]         = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm]  = useState(false);
  const [deleteLoading,     setDeleteLoading]      = useState(false);
  // ───────────────────────────────────────────────────────────────────────────

  const [loading,         setLoading]         = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [successMessage,  setSuccessMessage]  = useState('');
  const [errorMessage,    setErrorMessage]    = useState('');
  const [existingSections, setExistingSections] = useState([]); // sections already created for this product
  const [newSectionInput,  setNewSectionInput]  = useState(''); // when user types a brand new section
  const [sectionMode,      setSectionMode]      = useState('select'); // 'select' | 'new'
  // ── Per-file upload progress ──────────────────────────────────────────────
  // Each entry: { fieldName, label, fileName, progress (0-100), done, error }
  const [fileProgresses, setFileProgresses] = useState([]);
  const [isUploading,    setIsUploading]    = useState(false);

  const initFileProgresses = (fileEntries) => setFileProgresses(fileEntries);

  const updateFileProgress = (fieldName, patch) => {
    setFileProgresses(prev =>
      prev.map(f => f.fieldName === fieldName ? { ...f, ...patch } : f)
    );
  };

  // ── API endpoints ──────────────────────────────────────────────────────────
  const API_BASE_URL      = 'https://dolphin-app-33jp4.ondigitalocean.app';
  const API_FILE_UPLOAD   = `${API_BASE_URL}/file_upload/upload`;
  const API_MODAL_CREATE  = `${API_BASE_URL}/isfathena/modal/create`;
  const API_MODAL_UPDATE  = `${API_BASE_URL}/isfathena/modal/update`;   // ✅ NEW
  const API_MODAL_DELETE  = `${API_BASE_URL}/isfathena/modal/delete`;   // ✅ NEW
  const API_MODAL_GET     = `${API_BASE_URL}/isfathena/modal/get`;      // ✅ NEW
  const API_MODAL_LIST    = `${API_BASE_URL}/isfathena/modal/list`;     // fallback
  const API_PRODUCTS_LIST = `${API_BASE_URL}/isfathena/product/list`;
  const API_MODAL_SECTIONS = `${API_BASE_URL}/isfathena/modal/sections`; // ✅ NEW
  // ───────────────────────────────────────────────────────────────────────────

  // ── Fetch existing sections for the selected product ──────────────────────
  const fetchSections = async (productId) => {
    if (!productId) return;
    try {
      const res = await axios.get(`${API_MODAL_SECTIONS}?product_id=${productId}`);
      if (res.data.success) {
        setExistingSections(res.data.sections || []);
      }
    } catch (err) {
      console.warn('[AddModal] Could not fetch sections:', err.message);
    }
  };

  // ── On mount: read URL params ──────────────────────────────────────────────
  // Two cases:
  //   ?editId=X                   → Edit mode: reffrence.js edit button sends this
  //   ?productId=X&productName=Y  → Add mode: reffrence.js "Add Modal" button sends this
  useEffect(() => {
    const params      = new URLSearchParams(window.location.search);
    const editId      = params.get('editId');
    const productId   = params.get('productId');
    const productName = params.get('productName');

    if (editId) {
      // ← reffrence.js Edit button clicked → auto-load modal data into form
      loadModalForEdit(editId);
    } else if (productId && productName) {
      setFormData(prev => ({
        ...prev,
        productId,
        productName: decodeURIComponent(productName)
      }));
      setLoadingProducts(false);
      fetchSections(productId); // load existing sections
    } else {
      setLoadingProducts(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── loadModalForEdit ───────────────────────────────────────────────────────
  // Fetches modal data by ID and fills the form.
  // Tries /modal/get?id=X first; falls back to /modal/list if that endpoint
  // is not yet deployed.
  const loadModalForEdit = async (modalId) => {
    setLoading(true);
    setLoadingProducts(false);
    setErrorMessage('');
    try {
      let modal = null;

      // 1️⃣  Try the dedicated single-record endpoint
      try {
        const res = await axios.get(`${API_MODAL_GET}?id=${modalId}`);
        if (res.data.success && res.data.modal) {
          modal = res.data.modal;
        }
      } catch {
        console.warn('[AddModal] /modal/get failed, falling back to /modal/list');
      }

      // 2️⃣  Fallback: get full list and find by id
      if (!modal) {
        const listRes   = await axios.get(API_MODAL_LIST);
        const allModals = listRes.data.modals || listRes.data.modal || [];
        modal = allModals.find(m => String(m.id) === String(modalId));
      }

      if (!modal) {
        setErrorMessage(`Modal with ID "${modalId}" not found. Check the ID and try again.`);
        return;
      }

      // 3️⃣  Populate form with the fetched data
      setEditModalId(String(modalId));
      setIsEditMode(true);
      fetchSections(modal.product_id);
      setFormData({
        productId:   String(modal.product_id),
        productName: modal.product_name   || '',
        modalName:   modal.modal_name     || '',
        description: modal.description    || '',
        section:     modal.section        || '',
        documents: {
          documentType:      'link', document:      null, documentUrl:      modal.document       || '',
          bookDocumentType:  'link', bookDocument:  null, bookDocumentUrl:  modal.book           || '',
          referenceBookType: 'link', referenceBook: null, referenceBookUrl: modal.reference_book || '',
          socialMediaType:   'link', socialMedia:   null, socialMediaUrl:   modal.social_media   || '',
          websiteType:       'link', website:       null, websiteUrl:       modal.website        || '',
          pptType:           'link', ppt:           null, pptUrl:           modal.ppt            || ''
        }
      });

    } catch (error) {
      console.error('[AddModal] loadModalForEdit error:', error);
      setErrorMessage('Failed to load modal data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Cancel edit → return to Add mode ──────────────────────────────────────
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditModalId(null);
    setFormData({
      productId: '', productName: '', modalName: '', description: '',
      section: '',
      documents: {
        documentType: 'link', document: null, documentUrl: '',
        bookDocumentType: 'link', bookDocument: null, bookDocumentUrl: '',
        referenceBookType: 'link', referenceBook: null, referenceBookUrl: '',
        socialMediaType: 'link', socialMedia: null, socialMediaUrl: '',
        websiteType: 'link', website: null, websiteUrl: '',
        pptType: 'link', ppt: null, pptUrl: ''
      }
    });
    setSuccessMessage('');
    setErrorMessage('');
    // Remove editId from URL so a page refresh doesn't reload the old data
    window.history.replaceState({}, '', window.location.pathname);
  };

  // ── Delete handlers ────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!editModalId) return;
    setDeleteLoading(true);
    try {
      const fd = new FormData();
      fd.append('id', editModalId);
      const response = await axios.post(API_MODAL_DELETE, fd);
      if (response.data.success) {
        setShowDeleteConfirm(false);
        handleCancelEdit();
        setSuccessMessage('✅ Modal deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 4000);
      } else {
        setErrorMessage(response.data.message || 'Delete failed. Please try again.');
        setShowDeleteConfirm(false);
      }
    } catch (error) {
      console.error('Delete error:', error);
      setErrorMessage('Failed to delete modal. Please try again.');
      setShowDeleteConfirm(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Input handlers ─────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleTypeChange = (fieldName) => (e) => {
    setFormData({
      ...formData,
      documents: {
        ...formData.documents,
        [`${fieldName}Type`]: e.target.value,
        [fieldName]: null,
        [`${fieldName}Url`]: ''
      }
    });
  };

  const handleLinkChange = (fieldName) => (e) => {
    setFormData({
      ...formData,
      documents: { ...formData.documents, [`${fieldName}Url`]: e.target.value }
    });
  };

  const handleFileChange = (fieldName) => (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        documents: { ...formData.documents, [fieldName]: file, [`${fieldName}Url`]: file.name }
      });
    }
  };

  // ── S3 upload (per-file, updates its own progress entry) ─────────────────
  const uploadFileToS3 = async (file, fieldName) => {
    const fileFormData = new FormData();
    fileFormData.append('file', file);
    try {
      const response = await axios.post(API_FILE_UPLOAD, fileFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          updateFileProgress(fieldName, { progress: pct });
        }
      });
      if (response.data && response.data.file_url) {
        updateFileProgress(fieldName, { progress: 100, done: true });
        return response.data.file_url;
      }
      throw new Error('Upload failed - no URL returned');
    } catch (err) {
      updateFileProgress(fieldName, { error: true });
      throw err;
    }
  };

  // ── Build final URLs (upload files / keep links as-is) ────────────────────
  const FIELD_LABELS = {
    document:      'Document',
    bookDocument:  'Book / Link',
    referenceBook: 'Reference Book',
    socialMedia:   'Social Media',
    website:       'Website',
    ppt:           'PPT'
  };

  const buildFinalUrls = async () => {
    const fileFields = [
      { name: 'document',      typeField: 'documentType',      urlField: 'documentUrl' },
      { name: 'bookDocument',  typeField: 'bookDocumentType',  urlField: 'bookDocumentUrl' },
      { name: 'referenceBook', typeField: 'referenceBookType', urlField: 'referenceBookUrl' },
      { name: 'socialMedia',   typeField: 'socialMediaType',   urlField: 'socialMediaUrl' },
      { name: 'website',       typeField: 'websiteType',       urlField: 'websiteUrl' },
      { name: 'ppt',           typeField: 'pptType',           urlField: 'pptUrl' }
    ];

    // Build initial progress entries for files that need uploading
    const filesToUpload = fileFields.filter(
      f => formData.documents[f.typeField] === 'file' && formData.documents[f.name]
    );

    if (filesToUpload.length > 0) {
      setIsUploading(true);
      initFileProgresses(filesToUpload.map(f => ({
        fieldName: f.name,
        label:     FIELD_LABELS[f.name] || f.name,
        fileName:  formData.documents[f.name]?.name || f.name,
        progress:  0,
        done:      false,
        error:     false
      })));
    }

    const finalUrls = {};
    for (const field of fileFields) {
      const type = formData.documents[field.typeField];
      if (type === 'file' && formData.documents[field.name]) {
        finalUrls[field.urlField] = await uploadFileToS3(formData.documents[field.name], field.name);
      } else if (formData.documents[field.urlField]) {
        finalUrls[field.urlField] = formData.documents[field.urlField];
      }
    }

    setIsUploading(false);
    return finalUrls;
  };

  // ── Append document fields to FormData ────────────────────────────────────
  const appendDocFields = (fd, finalUrls) => {
    fd.append('documentType',      formData.documents.documentType);
    if (finalUrls.documentUrl)      fd.append('documentUrl',      finalUrls.documentUrl);
    fd.append('bookDocumentType',   formData.documents.bookDocumentType);
    if (finalUrls.bookDocumentUrl)  fd.append('bookDocumentUrl',  finalUrls.bookDocumentUrl);
    fd.append('referenceBookType',  formData.documents.referenceBookType);
    if (finalUrls.referenceBookUrl) fd.append('referenceBookUrl', finalUrls.referenceBookUrl);
    fd.append('socialMediaType',    formData.documents.socialMediaType);
    if (finalUrls.socialMediaUrl)   fd.append('socialMediaUrl',   finalUrls.socialMediaUrl);
    fd.append('websiteType',        formData.documents.websiteType);
    if (finalUrls.websiteUrl)       fd.append('websiteUrl',       finalUrls.websiteUrl);
    if (formData.documents.pptType) {
      fd.append('pptType', formData.documents.pptType);
      if (finalUrls.pptUrl) fd.append('pptUrl', finalUrls.pptUrl);
    }
  };

  // ── handleSubmit: ADD mode → /modal/create  |  EDIT mode → /modal/update ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const finalUrls      = await buildFinalUrls();
      const formDataToSend = new FormData();
      formDataToSend.append('productId',   formData.productId);
      formDataToSend.append('productName', formData.productName);
      formDataToSend.append('modalName',   formData.modalName);
      formDataToSend.append('description', formData.description);
      // section: use newSectionInput if user typed a new one, else the selected value
      const finalSection = sectionMode === 'new' ? newSectionInput.trim() : formData.section;
      if (finalSection) formDataToSend.append('section', finalSection);
      appendDocFields(formDataToSend, finalUrls);

      if (isEditMode && editModalId) {
        // ── UPDATE existing modal ───────────────────────────────────────────
        formDataToSend.append('id', editModalId);
        const response = await axios.post(API_MODAL_UPDATE, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data.success) {
          setSuccessMessage('✅ Modal updated successfully!');
          setTimeout(() => setSuccessMessage(''), 5000);
        } else {
          setErrorMessage(response.data.message || 'Failed to update modal');
        }
      } else {
        // ── CREATE new modal ────────────────────────────────────────────────
        const response = await axios.post(API_MODAL_CREATE, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data.success) {
          setSuccessMessage('✅ Modal created successfully!');
          // Reset form fields (keep product selection)
          setFileProgresses([]);
          setIsUploading(false);
          setFormData({
            productId:   formData.productId,
            productName: formData.productName,
            modalName: '', description: '',
            section: '',
            documents: {
              documentType: 'link', document: null, documentUrl: '',
              bookDocumentType: 'link', bookDocument: null, bookDocumentUrl: '',
              referenceBookType: 'link', referenceBook: null, referenceBookUrl: '',
              socialMediaType: 'link', socialMedia: null, socialMediaUrl: '',
              websiteType: 'link', website: null, websiteUrl: '',
              pptType: 'link', ppt: null, pptUrl: ''
            }
          });
          setTimeout(() => setSuccessMessage(''), 5000);
        } else {
          setErrorMessage(response.data.message || 'Failed to create modal');
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
      setErrorMessage(error.response?.data?.message || 'Operation failed. Please try again.');
    } finally {
      setLoading(false);
      setIsUploading(false);
    }
  };

  // ── FileUploadField sub-component ─────────────────────────────────────────
  const FileUploadField = ({ label, urlLabel, fieldName, urlFieldName }) => {
    const typeField   = `${fieldName}Type`;
    const currentType = formData.documents[typeField];
    const currentFile = formData.documents[fieldName];
    const currentUrl  = formData.documents[urlFieldName];

    return (
      <div className="mb-3 sm:mb-4">
        <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1">
          {label}:
        </label>
        <div className="flex flex-wrap gap-3 sm:gap-4 mb-2">
          <label className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-300 cursor-pointer">
            <input type="radio" name={typeField} value="link"
              checked={currentType === 'link'} onChange={handleTypeChange(fieldName)}
              className="w-3 h-3 sm:w-4 sm:h-4 text-orange-500 focus:ring-orange-500 focus:ring-offset-0" />
            Link
          </label>
          <label className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-300 cursor-pointer">
            <input type="radio" name={typeField} value="file"
              checked={currentType === 'file'} onChange={handleTypeChange(fieldName)}
              className="w-3 h-3 sm:w-4 sm:h-4 text-orange-500 focus:ring-orange-500 focus:ring-offset-0" />
            File
          </label>
        </div>

        {currentType === 'link' && (
          <input type="url" value={currentUrl} onChange={handleLinkChange(fieldName)}
            placeholder={urlLabel}
            className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-white/10 border border-white/20 rounded-lg text-xs sm:text-sm text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50 transition-all duration-300"
          />
        )}

        {currentType === 'file' && (
          <div className="relative">
            <input type="file" onChange={handleFileChange(fieldName)}
              accept=".pdf,.doc,.docx,.ppt,.pptx" className="hidden"
              id={`file-${fieldName}`} />
            <label htmlFor={`file-${fieldName}`}
              className="flex items-center justify-between w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-white/10 border border-white/20 rounded-lg cursor-pointer hover:border-orange-500 transition-all duration-300">
              <span className="text-xs sm:text-sm text-gray-400 truncate">
                {currentFile ? currentFile.name : 'Choose file...'}
              </span>
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 flex-shrink-0 ml-2"
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </label>
          </div>
        )}
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black pt-16 sm:pt-20 scrollbar-custom overflow-y-auto">
        <div className="max-w-3xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8 lg:py-10">

          {/* ── Mode Banner ────────────────────────────────────────────────── */}
          <div className="text-center mb-4 sm:mb-6 px-2">
            {isEditMode ? (
              /* Blue banner shown when editing an existing modal */
              <div className="mt-4 px-3 sm:px-4 py-2 bg-blue-500/20 border border-blue-500/50 rounded-lg inline-flex items-center gap-2 max-w-full flex-wrap justify-center">
                <svg className="w-4 h-4 text-blue-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <p className="text-blue-300 text-xs sm:text-sm break-all">
                  Editing Modal ID: <span className="font-bold text-blue-200">{editModalId}</span>
                  {formData.productName && (
                    <> — <span className="font-bold text-blue-200">{formData.productName}</span></>
                  )}
                </p>
              </div>
            ) : (
              /* Orange banner shown in Add mode when a product is pre-selected */
              formData.productId && formData.productName && (
                <div className="mt-4 px-3 sm:px-4 py-2 bg-orange-500/20 border border-orange-500/50 rounded-lg inline-block max-w-full">
                  <p className="text-orange-300 text-xs sm:text-sm break-words">
                    Add Document for:{' '}
                    <span className="font-bold text-orange-200">{formData.productName}</span>
                  </p>
                </div>
              )
            )}
          </div>

          {/* ── Loading spinner (fetching edit data from API) ─────────────── */}
          {loading && !isUploading && fileProgresses.length === 0 && (
            <div className="mb-4 p-4 bg-blue-500/20 border border-blue-500/50 rounded-lg flex items-center gap-3">
              <svg className="w-5 h-5 text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
              </svg>
              <p className="text-blue-300 text-sm">Loading modal data...</p>
            </div>
          )}

          {/* ── Success Message ───────────────────────────────────────────── */}
          {successMessage && (
            <div className="mb-4 p-4 bg-green-500/20 border border-green-500/50 rounded-lg">
              <p className="text-green-300 text-sm sm:text-base text-center">{successMessage}</p>
            </div>
          )}

          {/* ── Error Message ─────────────────────────────────────────────── */}
          {errorMessage && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg flex items-start justify-between gap-3">
              <p className="text-red-300 text-sm sm:text-base">{errorMessage}</p>
              <button onClick={() => setErrorMessage('')}
                className="text-red-400 hover:text-red-300 text-xl leading-none flex-shrink-0">×</button>
            </div>
          )}

          {/* ── Delete Confirm Dialog ─────────────────────────────────────── */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4 py-6">
              <div className="bg-gray-900 border border-red-500/50 rounded-2xl p-5 sm:p-6 w-full max-w-sm shadow-2xl mx-auto">
                <div className="flex justify-center mb-4">
                  <div className="w-14 h-14 bg-red-500/20 rounded-full flex items-center justify-center">
                    <svg className="w-7 h-7 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-white font-bold text-lg text-center mb-2">Delete Modal?</h3>
                <p className="text-gray-400 text-sm text-center mb-6">
                  Permanently delete Modal ID{' '}
                  <span className="text-red-400 font-bold">{editModalId}</span>?
                  This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button onClick={() => setShowDeleteConfirm(false)} disabled={deleteLoading}
                    className="flex-1 px-4 py-2.5 border border-white/20 text-gray-300 rounded-xl text-sm font-semibold hover:bg-white/10 transition-all disabled:opacity-50">
                    Cancel
                  </button>
                  <button onClick={handleDeleteConfirm} disabled={deleteLoading}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl text-sm font-bold hover:from-red-700 hover:to-red-800 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                    {deleteLoading ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                        </svg>
                        Deleting...
                      </>
                    ) : 'Yes, Delete'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Form Container ────────────────────────────────────────────── */}
          <div className="bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 lg:p-8 border border-white/20 shadow-2xl mb-6">
            <form onSubmit={handleSubmit}>

              {/* Product Name */}
              <div className="mb-3 sm:mb-4">
                <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1">
                  Product Name: <span className="text-orange-500">*</span>
                </label>
                {loadingProducts ? (
                  <div className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-white/10 border border-white/20 rounded-lg text-xs sm:text-sm text-gray-400">
                    Loading...
                  </div>
                ) : (
                  <div className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-white/10 border border-orange-500 rounded-lg text-xs sm:text-sm text-white font-semibold">
                    {formData.productName || 'No Product Selected'}
                  </div>
                )}
                {!formData.productId && !loadingProducts && (
                  <p className="text-xs text-orange-400 mt-1">
                    Please select a product from the Reference page first.
                  </p>
                )}
                <input type="hidden" name="productId" value={formData.productId} required />
              </div>

              {/* Model Name */}
              <div className="mb-3 sm:mb-4">
                <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1">
                  Document Name:
                </label>
                <input type="text" name="modalName" value={formData.modalName}
                  onChange={handleInputChange} placeholder="Document Name"
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-white/10 border border-white/20 rounded-lg text-xs sm:text-sm text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50 transition-all duration-300"
                />
              </div>

              {/* Section */}
              <div className="mb-3 sm:mb-4">
                <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1">
                  Section: <span className="text-gray-500 text-xs">(optional — groups models in Reference page)</span>
                </label>

                {/* Toggle: Select existing / Create new */}
                <div className="flex flex-wrap gap-3 sm:gap-4 mb-2">
                  <label className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-300 cursor-pointer">
                    <input type="radio" name="sectionMode" value="select"
                      checked={sectionMode === 'select'}
                      onChange={() => setSectionMode('select')}
                      className="w-3 h-3 sm:w-4 sm:h-4 text-orange-500 focus:ring-orange-500 focus:ring-offset-0" />
                    {existingSections.length > 0 ? 'Select existing' : 'No sections yet'}
                  </label>
                  <label className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-300 cursor-pointer">
                    <input type="radio" name="sectionMode" value="new"
                      checked={sectionMode === 'new'}
                      onChange={() => setSectionMode('new')}
                      className="w-3 h-3 sm:w-4 sm:h-4 text-orange-500 focus:ring-orange-500 focus:ring-offset-0" />
                    Create new section
                  </label>
                </div>

                {sectionMode === 'select' && (
                  <select
                    value={formData.section}
                    onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-white/10 border border-white/20 rounded-lg text-xs sm:text-sm text-white focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50 transition-all duration-300"
                    style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>
                    <option value="" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>— No section (ungrouped) —</option>
                    {existingSections.map((s) => (
                      <option key={s} value={s} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>{s}</option>
                    ))}
                  </select>
                )}

                {sectionMode === 'new' && (
                  <input
                    type="text"
                    value={newSectionInput}
                    onChange={(e) => setNewSectionInput(e.target.value)}
                    placeholder='e.g. "Company Information"'
                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-white/10 border border-orange-500/60 rounded-lg text-xs sm:text-sm text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50 transition-all duration-300"
                  />
                )}
              </div>

              {/* Document upload fields */}
              <FileUploadField label="Document"        urlLabel="Document (As PDF)"           fieldName="document"      urlFieldName="documentUrl" />
              <FileUploadField label="Book / Link"     urlLabel="Book Document (PDF)"          fieldName="bookDocument"  urlFieldName="bookDocumentUrl" />
              <FileUploadField label="Reference Book"  urlLabel="Reference Document (PDF)"     fieldName="referenceBook" urlFieldName="referenceBookUrl" />
              <FileUploadField label="Social Media"    urlLabel="Social Media Document (PDF)"  fieldName="socialMedia"   urlFieldName="socialMediaUrl" />
              <FileUploadField label="Website"         urlLabel="Website Document (PDF)"       fieldName="website"       urlFieldName="websiteUrl" />
              <FileUploadField label="PPT (Optional)"  urlLabel="PPT Document"                 fieldName="ppt"           urlFieldName="pptUrl" />

              {/* Description */}
              <div className="mb-4 sm:mb-5">
                <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1">
                  Description:
                </label>
                <textarea name="description" value={formData.description}
                  onChange={handleInputChange} placeholder="Description" rows="3"
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-white/10 border border-white/20 rounded-lg text-xs sm:text-sm text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50 transition-all duration-300 resize-none"
                ></textarea>
              </div>

              {/* ── Per-file Upload Progress (shown at bottom of form while uploading) ── */}
              {isUploading && fileProgresses.length > 0 && (
                <div className="mb-5 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl space-y-3">
                  <p className="text-blue-300 text-sm font-semibold flex items-center gap-2">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                    </svg>
                    Uploading files to server...
                  </p>
                  {fileProgresses.map((f) => (
                    <div key={f.fieldName}>
                      {/* File name + label + percentage */}
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 min-w-0">
                          {f.done ? (
                            <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : f.error ? (
                            <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4 text-blue-400 flex-shrink-0 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                            </svg>
                          )}
                          <span className="text-xs font-semibold text-gray-300 flex-shrink-0">{f.label}:</span>
                          <span className="text-xs text-gray-400 truncate">{f.fileName}</span>
                        </div>
                        <span className={`text-xs font-bold flex-shrink-0 ml-2 ${
                          f.done ? 'text-green-400' : f.error ? 'text-red-400' : 'text-blue-300'
                        }`}>
                          {f.error ? 'Failed' : f.done ? '✓ Done' : `${f.progress}%`}
                        </span>
                      </div>
                      {/* Progress bar */}
                      <div className="w-full bg-gray-700/60 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all duration-200 ${
                            f.error ? 'bg-red-500' : f.done ? 'bg-green-500' : 'bg-blue-500'
                          }`}
                          style={{ width: `${f.error ? 100 : f.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                  {/* Overall summary */}
                  <div className="pt-2 border-t border-blue-500/20 flex items-center justify-between">
                    <span className="text-xs text-blue-400">
                      {fileProgresses.filter(f => f.done).length} / {fileProgresses.length} files complete
                    </span>
                    <span className="text-xs text-blue-300 font-bold">
                      {Math.round(fileProgresses.reduce((sum, f) => sum + f.progress, 0) / fileProgresses.length)}% overall
                    </span>
                  </div>
                </div>
              )}

              {/* ── Action Buttons ──────────────────────────────────────── */}
              <div className="flex flex-col xs:flex-row flex-wrap items-center justify-center gap-2 sm:gap-3">

                {/* ① ADD MODEL / SAVE CHANGES ─────────────────────────────── */}
                <button type="submit" disabled={loading}
                  className={`group relative flex items-center justify-center gap-2 w-full xs:w-auto px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 font-bold text-sm sm:text-base rounded-xl transition-all duration-300 text-white
                    ${isEditMode
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                      : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700'
                    }
                    ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 hover:shadow-2xl'}`}>
                  {isEditMode ? (
                    <>
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      {loading ? 'Saving...' : 'Save Changes'}
                    </>
                  ) : (
                    <>
                      <span className="relative z-10">{loading ? 'Uploading & Saving...' : 'Add Document'}</span>
                      {!loading && (
                        <div className="absolute inset-0 rounded-xl bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                      )}
                    </>
                  )}
                </button>

                {/* ② EDIT button — visible in Add mode, prompts for Modal ID ─ */}
                {!isEditMode && (
                  <button type="button" disabled={loading}
                    onClick={() => {
                      const id = prompt('Enter the Modal ID to edit:');
                      if (id && !isNaN(id.trim())) {
                        loadModalForEdit(id.trim());
                      } else if (id !== null) {
                        setErrorMessage('Please enter a valid numeric Modal ID.');
                      }
                    }}
                    className="flex items-center justify-center gap-2 w-full xs:w-auto px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold text-sm sm:text-base rounded-xl hover:from-blue-600 hover:to-blue-700 hover:scale-105 hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                  </button>
                )}

                {/* ③ DELETE — visible only in Edit mode ────────────────────── */}
                {isEditMode && (
                  <button type="button" disabled={loading || deleteLoading}
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center justify-center gap-2 w-full xs:w-auto px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-red-600 to-red-700 text-white font-bold text-sm sm:text-base rounded-xl hover:from-red-700 hover:to-red-800 hover:scale-105 hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                )}

                {/* ④ CANCEL — visible only in Edit mode ───────────────────── */}
                {isEditMode && (
                  <button type="button" disabled={loading}
                    onClick={handleCancelEdit}
                    className="flex items-center justify-center gap-2 w-full xs:w-auto px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 border border-white/30 text-gray-300 font-bold text-sm sm:text-base rounded-xl hover:bg-white/10 hover:border-white/50 transition-all duration-300 disabled:opacity-50">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Cancel
                  </button>
                )}

              </div>
              {/* ──────────────────────────────────────────────────────────── */}

            </form>
          </div>
        </div>
      </div>

      <style jsx>{`
        .scrollbar-custom::-webkit-scrollbar { width: 6px; }
        @media (min-width: 640px) { .scrollbar-custom::-webkit-scrollbar { width: 8px; } }
        .scrollbar-custom::-webkit-scrollbar-track { background: rgba(255,255,255,0.05); border-radius: 10px; }
        .scrollbar-custom::-webkit-scrollbar-thumb { background: #f97316; border-radius: 10px; }
        .scrollbar-custom::-webkit-scrollbar-thumb:hover { background: #ea580c; }
        .scrollbar-custom { scrollbar-width: thin; scrollbar-color: #f97316 rgba(255,255,255,0.05); }
        @media (min-width: 480px) {
          .xs\\:flex-row { flex-direction: row; }
          .xs\\:w-auto { width: auto; }
        }
      `}</style>
    </>
  );
}

export default AddModal;